<template>
    <div class="security-redirect-page" data-cy="security-redirect">
        <div class="security-redirect-sky" aria-hidden="true">
            <span class="security-redirect-sun"></span>
        </div>
        <div class="security-redirect-sea" aria-hidden="true">
            <span class="security-redirect-wave security-redirect-wave-back"></span>
            <span class="security-redirect-wave security-redirect-wave-middle"></span>
            <span class="security-redirect-wave security-redirect-wave-front"></span>
        </div>

        <div class="security-redirect-card" :class="{'is-error': hasError}">
            <div class="security-redirect-route">
                <span>{{ t('SECURITY_LOGIN.FROM') }}</span>
                <span class="security-redirect-route-line"><i></i></span>
                <span>{{ t('SECURITY_LOGIN.TO') }}</span>
            </div>

            <div class="security-redirect-scene">
                <div class="security-redirect-halo"></div>
                <img class="security-redirect-duck" src="@/assets/img/redirect-duck.png"
                     :alt="t('SECURITY_LOGIN.TITLE')">
                <div class="security-redirect-ripples">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>

            <template v-if="!hasError">
                <h1 class="security-redirect-title">{{ t('SECURITY_LOGIN.TITLE') }}</h1>
                <p class="security-redirect-subtitle">{{ t('SECURITY_LOGIN.SUBTITLE') }}</p>

                <ol class="security-redirect-steps">
                    <li v-for="(label, index) in steps" :key="label"
                        :class="{'is-done': index < currentStep, 'is-active': index === currentStep}">
                        <span class="security-redirect-step-icon">
                            <i v-if="index < currentStep" class="fas fa-check"></i>
                        </span>
                        {{ t(label) }}
                    </li>
                </ol>

                <div class="security-redirect-progress"><span></span></div>
            </template>

            <template v-else>
                <h1 class="security-redirect-title">{{ t('SECURITY_LOGIN.ERROR_TITLE') }}</h1>
                <p class="security-redirect-subtitle">{{ t('SECURITY_LOGIN.ERROR_SUBTITLE') }}</p>
                <button type="button" class="btn security-redirect-button" data-cy="security-redirect-back"
                        @click="$router.push({name: 'Login'})">
                    {{ t('SECURITY_LOGIN.BACK_TO_LOGIN') }}
                </button>
            </template>
        </div>
    </div>
</template>

<script>

import User from "@/services/user";
import stores from "@/vuex";
import jwt_decode from 'jwt-decode';
import {useI18n} from "vue-i18n";

export default {
    name: 'SecurityLogin',
    setup() {
        const {t} = useI18n();
        return {t}
    },

    data() {
        return {
            currentStep: 0,
            hasError: false,
            steps: [
                'SECURITY_LOGIN.STEP_VALIDATING',
                'SECURITY_LOGIN.STEP_COMPANIES',
                'SECURITY_LOGIN.STEP_OPENING',
            ],
        }
    },

    mounted() {
        this.loginUser();
    },

    methods: {
        async loginUser() {
            let token = this.$route.params.token;
            await this.$store.dispatch('ActionSingOut');
            User.security(token).then(async (resp) => {
                this.currentStep = 1;
                let userId = jwt_decode(resp.data.accessToken).idUsuario;
                await User.getCompaniesUser(userId, resp.data.accessToken).then(companies => {
                    this.currentStep = 2;
                    resp.data.companies = companies.data;
                    resp.data.companyId = resp.data.connection.uuid_exposed;
                    stores.dispatch('ActionDoLoginSecurity', resp.data);
                    this.$router.push({name: 'Home'});
                })
            }).catch(() => {
                this.hasError = true;
            })
        }
    }


}
</script>

<style scoped>
.security-redirect-page {
    --sr-primary: #0e5e4e;
    --sr-accent: #14b8a6;
    --sr-text: #0f172a;
    --sr-text-soft: #475569;
    --sr-text-muted: #64748b;
    --sr-border: rgba(15, 94, 78, 0.14);
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    overflow-y: auto;
    color: var(--sr-text);
    background: linear-gradient(180deg, #f4fbf9 0%, #e3f4ef 45%, #cdebe3 70%, #a9dccf 100%);
}

.security-redirect-sky,
.security-redirect-sea {
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
}

.security-redirect-sky {
    top: 0;
    height: 70%;
    overflow: hidden;
}

.security-redirect-sun {
    position: absolute;
    top: -120px;
    right: 8%;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 244, 214, 0.95) 0%, rgba(255, 236, 190, 0.45) 30%, transparent 68%);
    animation: sr-glow 6s ease-in-out infinite;
}

.security-redirect-sea {
    bottom: 0;
    height: 34%;
    min-height: 200px;
}

.security-redirect-wave {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background-repeat: repeat-x;
    background-position: 0 bottom;
    animation: sr-wave linear infinite;
}

.security-redirect-wave-back {
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320' preserveAspectRatio='none'%3E%3Cpath fill='%2314b8a6' fill-opacity='0.18' d='M0 80 C360 0 1080 160 1440 80 L1440 320 L0 320 Z'/%3E%3C/svg%3E");
    background-size: 1440px 100%;
    --sr-wave-width: 1440px;
    animation-duration: 38s;
}

.security-redirect-wave-middle {
    height: 78%;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 320' preserveAspectRatio='none'%3E%3Cpath fill='%230f8f7d' fill-opacity='0.22' d='M0 70 C300 150 900 -10 1200 70 L1200 320 L0 320 Z'/%3E%3C/svg%3E");
    background-size: 1200px 100%;
    --sr-wave-width: 1200px;
    animation-duration: 26s;
    animation-direction: reverse;
}

.security-redirect-wave-front {
    height: 55%;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 960 320' preserveAspectRatio='none'%3E%3Cpath fill='%230e5e4e' fill-opacity='0.24' d='M0 80 C240 -10 720 170 960 80 L960 320 L0 320 Z'/%3E%3C/svg%3E");
    background-size: 960px 100%;
    --sr-wave-width: 960px;
    animation-duration: 18s;
}

.security-redirect-card {
    position: relative;
    z-index: 1;
    width: min(500px, 100%);
    padding: 32px 36px 36px;
    border: 1px solid var(--sr-border);
    border-radius: 32px;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 28px 80px rgba(15, 23, 42, 0.14);
    backdrop-filter: blur(22px) saturate(120%);
    text-align: center;
    animation: sr-enter 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.security-redirect-route {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    border: 1px solid var(--sr-border);
    border-radius: 999px;
    background: rgba(14, 94, 78, 0.06);
    color: var(--sr-primary);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.security-redirect-route-line {
    position: relative;
    width: 44px;
    height: 2px;
    border-radius: 2px;
    background: rgba(14, 94, 78, 0.18);
    overflow: hidden;
}

.security-redirect-route-line i {
    position: absolute;
    top: 0;
    left: -40%;
    width: 40%;
    height: 100%;
    border-radius: 2px;
    background: var(--sr-accent);
    animation: sr-travel 1.6s ease-in-out infinite;
}

.security-redirect-scene {
    position: relative;
    margin: 22px auto 14px;
    width: 100%;
    max-width: 384px;
}

.security-redirect-halo {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(94, 234, 212, 0.35), transparent 68%);
    transform: translate(-50%, -50%);
}

.security-redirect-duck {
    position: relative;
    display: block;
    width: 100%;
    height: auto;
    animation: sr-float 3.2s ease-in-out infinite;
}

.security-redirect-ripples {
    position: absolute;
    left: 50%;
    bottom: 16px;
    width: 180px;
    height: 24px;
    transform: translateX(-50%);
    pointer-events: none;
}

.security-redirect-ripples span {
    position: absolute;
    inset: 0;
    border: 2px solid rgba(20, 184, 166, 0.45);
    border-radius: 50%;
    opacity: 0;
    animation: sr-ripple 3s ease-out infinite;
}

.security-redirect-ripples span:nth-child(2) {
    animation-delay: 1s;
}

.security-redirect-ripples span:nth-child(3) {
    animation-delay: 2s;
}

.security-redirect-title {
    margin: 0 0 8px;
    color: var(--sr-text) !important;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.02em;
}

.security-redirect-subtitle {
    margin: 0 auto 24px;
    max-width: 340px;
    color: var(--sr-text-soft) !important;
    font-size: 14px;
    line-height: 1.55;
}

.security-redirect-steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 auto 24px;
    padding: 0;
    width: fit-content;
    list-style: none;
    text-align: left;
}

.security-redirect-steps li {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--sr-text-muted);
    font-size: 14px;
    transition: color 0.3s;
}

.security-redirect-step-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(100, 116, 139, 0.35);
    border-radius: 50%;
    color: #ffffff;
    font-size: 10px;
    transition: background 0.3s, border-color 0.3s;
}

.security-redirect-steps li.is-active {
    color: var(--sr-text);
    font-weight: 600;
}

.security-redirect-steps li.is-active .security-redirect-step-icon {
    border-color: rgba(20, 184, 166, 0.25);
    border-top-color: var(--sr-accent);
    animation: sr-spin 0.9s linear infinite;
}

.security-redirect-steps li.is-done {
    color: var(--sr-primary);
}

.security-redirect-steps li.is-done .security-redirect-step-icon {
    border-color: var(--sr-accent);
    background: var(--sr-accent);
}

.security-redirect-progress {
    position: relative;
    height: 4px;
    border-radius: 4px;
    background: rgba(14, 94, 78, 0.10);
    overflow: hidden;
}

.security-redirect-progress span {
    position: absolute;
    top: 0;
    left: -35%;
    width: 35%;
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, var(--sr-accent), var(--sr-primary));
    animation: sr-travel 1.4s ease-in-out infinite;
}

.security-redirect-button {
    padding: 10px 24px;
    border-radius: 12px;
    background: var(--sr-primary);
    color: #ffffff !important;
    font-weight: 700;
}

.security-redirect-button:hover {
    background: #0b4c40;
}

.is-error .security-redirect-duck,
.is-error .security-redirect-ripples span,
.is-error .security-redirect-route-line i {
    animation-play-state: paused;
}

.is-error .security-redirect-duck {
    filter: grayscale(0.4);
    opacity: 0.85;
}

@keyframes sr-enter {
    from {
        opacity: 0;
        transform: translateY(16px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: none;
    }
}

@keyframes sr-float {
    0%, 100% {
        transform: translateY(0) rotate(0);
    }
    50% {
        transform: translateY(-7px) rotate(-1.5deg);
    }
}

@keyframes sr-ripple {
    from {
        opacity: 0.8;
        transform: scale(0.5);
    }
    to {
        opacity: 0;
        transform: scale(1.4);
    }
}

@keyframes sr-travel {
    from {
        left: -40%;
    }
    to {
        left: 100%;
    }
}

@keyframes sr-wave {
    to {
        background-position-x: var(--sr-wave-width, 1440px);
    }
}

@keyframes sr-glow {
    0%, 100% {
        opacity: 0.85;
    }
    50% {
        opacity: 1;
    }
}

@keyframes sr-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 480px) {
    .security-redirect-card {
        padding: 24px 20px 28px;
        border-radius: 24px;
    }

    .security-redirect-title {
        font-size: 20px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .security-redirect-page *,
    .security-redirect-card {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
